/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getAccessToken,
  getCompanyIdFromToken,
  subscribeToToken,
} from 'libs/embed-auth';

interface EmbedAuthContextValue {
  /** True once the parent has relayed an access token. */
  isAuthenticated: boolean;
  /** Company id from the token claim (`CompanyId`/`company_id`); null until received. */
  companyId: string | null;
}

const EmbedAuthContext = createContext<EmbedAuthContextValue>({
  isAuthenticated: false,
  companyId: null,
});

export const EmbedAuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(getAccessToken());

  useEffect(() => {
    const current = getAccessToken();
    if (current) {
      setToken(current);
      return undefined;
    }
    return subscribeToToken((next) => setToken(next));
  }, []);

  const value = useMemo<EmbedAuthContextValue>(
    () => ({
      isAuthenticated: !!token,
      companyId: token ? getCompanyIdFromToken(token) : null,
    }),
    [token],
  );

  return (
    <EmbedAuthContext.Provider value={value}>
      {children}
    </EmbedAuthContext.Provider>
  );
};

export const useEmbedAuth = (): EmbedAuthContextValue =>
  useContext(EmbedAuthContext);
